import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './lesson.module.scss';
import ArrowTop from '../../../assets/icons/chewron_top.svg?react';
import { Lesson as LessonType } from '../../../types';
import { useCourseContent } from '../../../hooks/useCourseContent';
import { LastVisitedLesson } from '../ProgramDetails';
import SectionContents from './lesson-section/section-contents/SectionContents';
import { useAnalytics, Events, useUser } from '@hooks';

type Props = {
  lesson: LessonType;
  lastVisitedLesson?: LastVisitedLesson;
  isParentApp?: boolean;
  closeNav?: () => void;
};

function Lesson(props: Props) {
  const { lesson, lastVisitedLesson, isParentApp, closeNav } = props;
  const [opened, setOpened] = useState(false);
  const { content, lesson: selectedLesson, setLesson, setContent, setSelectionLevel, program, setIsParent } = useCourseContent();
  const [user] = useUser();
  const { trackEvent } = useAnalytics();

  const selected = selectedLesson?.id === lesson.id;
  const contentSelectedInLesson = useMemo(() => {
    if (!content) return false;
    return [];
  }, [content]);

  const wrapperClasses = useMemo(() => {
    const classes = [styles.wrapper];

    if (opened) {
      classes.push(styles.opened);
    }

    if (selected) {
      classes.push(styles.selected);
    }

    return classes.join(' ');
  }, [opened, selected]);

  useEffect(() => {
    setIsParent(!!isParentApp);
  }, [isParentApp, setIsParent]);

  useEffect(() => {
    if (lastVisitedLesson && lastVisitedLesson.lessonId === lesson.id && !opened) {
      setOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastVisitedLesson]);

  useEffect(() => {
    if (selectedLesson?.id === lesson.id) {
      setOpened(true);
    }
  }, [selectedLesson, lesson]);

  const handleSelectLesson = useCallback(() => {
    setLesson(lesson);
    setContent(undefined);
    if (opened) {
      setOpened(false);
      setSelectionLevel('Lesson');
      return;
    }
    if (lesson.id) {
      setSelectionLevel('Lesson');
    } else {
      setSelectionLevel('Content');
    }
    if (!opened && !isParentApp) {
      setOpened(true);
      const _d = {
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(lesson),
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };
      trackEvent(Events.actions.courseLessonOpen, _d);
    }
  }, [setLesson, lesson, setContent, opened, isParentApp, setSelectionLevel, program, trackEvent, user]);

  const handleClose = () => {
    setOpened(!opened);
  };

  const contentClasses = useMemo(() => {
    const classes = [styles.content];

    if (contentSelectedInLesson) {
      classes.push(styles.contentSelected);
    }

    return classes.join(' ');
  }, [contentSelectedInLesson]);

  return (
    <div className={wrapperClasses}>
      <section onClick={handleSelectLesson} className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.title}>{lesson.name}</div>
        </div>
        <div className={styles.iconWrapper} onClick={handleClose}>
          <ArrowTop className={styles.icon}/>
        </div>
      </section>
      {lesson.id ?
        <section className={contentClasses}>
          <SectionContents lesson={lesson} lastVisitedLesson={lastVisitedLesson} contents={lesson?.modules || []} closeNav={closeNav} />
        </section> : null}
    </div>
  );
}

export default Lesson;
